import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { HumanCheck } from "./HumanCheck";
import { CONTACT_INFO } from "../i18n";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING_PREFILL = {
  sk: "Dobrý deň, mám záujem o cenník certifikácie / služieb. Prosím o zaslanie cenovej ponuky.",
  en: "Hello, I'm interested in the price list for certification / services. Please send me a quote.",
  de: "Guten Tag, ich interessiere mich für die Preisliste für Zertifizierung / Leistungen. Bitte senden Sie mir ein Angebot.",
  pl: "Dzień dobry, jestem zainteresowany cennikiem certyfikacji / usług. Proszę o przesłanie oferty.",
  fr: "Bonjour, je suis intéressé par la grille tarifaire de la certification / des services. Merci de m'envoyer un devis.",
};

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [hToken, setHToken] = useState("");
  const [hAnswer, setHAnswer] = useState("");
  const [hReload, setHReload] = useState(0);

  useEffect(() => {
    const inquiry = new URLSearchParams(window.location.search).get("inquiry");
    if (inquiry === "pricing") {
      setForm((f) => (f.message ? f : { ...f, message: PRICING_PREFILL[lang] || PRICING_PREFILL.sk }));
    }
  }, [lang]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error(t.contact.consentRequired); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang, human_token: hToken, human_answer: hAnswer });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", message: "", website: "", consent: false });
      setHAnswer("");
      setHReload((x) => x + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.contact.error);
      setHAnswer("");
      setHReload((x) => x + 1);
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { Icon: Phone, label: t.contact.phone, value: CONTACT_INFO.phone, href: `tel:${CONTACT_INFO.phoneRaw}` },
    { Icon: Mail, label: t.contact.mail, value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
    { Icon: MapPin, label: t.contact.visit, value: CONTACT_INFO.address, href: CONTACT_INFO.mapQuery },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative z-10 py-24 md:py-32 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-24">
        <Reveal>
          <Overline testId="contact-overline">{t.contact.overline}</Overline>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
            {t.contact.title}
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mt-6 max-w-md">
            {t.contact.text}
          </p>

          <div className="mt-12 space-y-px bg-white/10">
            {info.map(({ Icon, label, value, href }, i) => (
              <a
                key={i}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={`contact-info-${i}`}
                className="group flex items-center gap-5 bg-[#0a0a0a] p-5 hover:bg-[#111] transition-colors duration-300"
              >
                <span className="w-11 h-11 shrink-0 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-colors duration-300">
                  <Icon size={18} strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block text-xs tracking-[0.2em] uppercase text-white/40">
                    {label}
                  </span>
                  <span className="text-white/90">{value}</span>
                </span>
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-[#050505] border border-white/10 p-8 md:p-10"
          >
            <div className="space-y-7">
              <Field label={t.contact.name} testId="contact-name-input" value={form.name} onChange={set("name")} required />
              <Field label={t.contact.email} type="email" testId="contact-email-input" value={form.email} onChange={set("email")} required />
              <Field label={t.contact.company} testId="contact-company-input" value={form.company} onChange={set("company")} />
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-white/40 mb-3">
                  {t.contact.message}
                </label>
                <textarea
                  data-testid="contact-message-textarea"
                  value={form.message}
                  onChange={set("message")}
                  required
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors resize-none placeholder:text-white/20"
                />
              </div>

              {/* Honeypot anti-spam field (hidden from real users) */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={set("website")}
                aria-hidden="true"
                className="hidden"
              />

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="contact-consent-checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  required
                  className="mt-1 w-4 h-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                />
                <span className="text-white/50 text-sm leading-relaxed">
                  {t.contact.consent}{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#D4AF37] hover:text-[#F2E3C6] underline underline-offset-2"
                  >
                    {t.contact.consentLink}
                  </a>
                  .
                </span>
              </label>

              <HumanCheck lang={lang} answer={hAnswer} onAnswer={setHAnswer} onToken={setHToken} reloadKey={hReload} />

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading || !form.consent || !hAnswer.trim()}
                className="group w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold py-4 hover:bg-[#F2E3C6] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? t.contact.sending : t.contact.submit}
                {!loading && (
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                )}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, testId, type = "text", value, onChange, required }) => (
  <div>
    <label className="block text-xs tracking-[0.2em] uppercase text-white/40 mb-3">
      {label}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/20"
    />
  </div>
);
